import { IField } from '../';
import { AppUtils, Data, DateForPmis } from './';

export class Pmis3JsonHelper {
    private static _copyDate(dt: Date): Date {
        if (!dt || !(dt instanceof Date)) return dt;
        return new DateForPmis(dt);
    }

    public static DeepCopy(obj: any): any {
        return this._deepCopyJsonData(obj, []);
    }

    private static _deepCopyJsonData(
        obj: any,
        clonedObjList: { origin: any; cloned: any }[]
    ) {
        if (obj === undefined || obj === null || !(obj instanceof Object))
            return obj;

        if (obj instanceof Date) return new DateForPmis(obj);

        //To prevent circle loop.
        let clonedObj = clonedObjList.find((p) => p.origin === obj);
        if (clonedObj) {
            console.warn('Deep copy circle loop!', obj);
            return clonedObj.cloned;
        }

        clonedObj = { origin: obj, cloned: undefined };
        clonedObjList.push(clonedObj);
        if (obj instanceof Array) {
            const arr: any[] = [];
            clonedObj.cloned = arr;
            arr.push(
                ...obj.map((p) => this._deepCopyJsonData(p, clonedObjList))
            );
        } else {
            clonedObj.cloned = {};
            Object.keys(obj).forEach((key) => {
                let cobj = obj[key];
                if (key !== 'updatedByUser' && key !== 'createdByUser')
                    cobj = this._deepCopyJsonData(cobj, clonedObjList);
                clonedObj.cloned[key] = cobj;
            });
        }
        return clonedObj.cloned;
    }

    public static CheckJsonObject(d: any, ignoreDateParsing = false) {
        if (!d || typeof d !== 'object') return;

        try {
            let jd: object | undefined = undefined;
            for (const i in d) {
                let di = d[i];
                if (i === 'jsonData' && !d.jData) {
                    di = di || '{}';
                    jd = JSON.parse(di);
                    this.CheckJsonObject(jd, ignoreDateParsing);
                    continue;
                }

                if (!di) continue;

                const type = typeof di;
                if (type === 'object') {
                    this.CheckJsonObject(d[i], ignoreDateParsing);
                } else if (type === 'string') {
                    //Check if Date
                    let checked = false;
                    if (
                        !ignoreDateParsing &&
                        di.length >= 16 &&
                        di.length < 30
                    ) {
                        const dt = AppUtils.DateTime.CheckStringIsUtcDate(di);
                        if (dt) {
                            d[i] = dt;
                            checked = true;
                        }
                    }
                    if (!checked && Data.isGuid(di)) {
                        checked = true;
                        d[i] = di.toLowerCase();
                    }
                }
            }
            if (jd) {
                d.jData = jd;
            }
        } catch (err) {
            console.log(err, d);
        }
    }

    public static copySimpleValueWithJData(obj: any): any {
        if (!obj || typeof obj !== 'object') return obj;

        if (obj instanceof Date) return this._copyDate(obj);

        const obj2 = { ...obj };
        Object.keys(obj2).forEach((k: string) => {
            const v = obj2[k];
            if (typeof v === 'object') {
                if (v instanceof Date) obj2[k] = this._copyDate(v);
                else if (k === 'jData') obj2[k] = this.DeepCopy(v);
                else delete obj2[k];
            }
        });
        return obj2;
    }

    public static getValueByPath(jo: any, path?: string): any {
        if (jo === null || jo === undefined || !path) return jo;

        return path.split('.').reduce((current, key) => {
            return current?.[key];
        }, jo);
    }

    public static getValueByField(obj: any, field: IField): any {
        if (obj === null || obj === undefined  || !field?.field) {
            return obj;
        }
        if (!field.fieldPath) {
            field.fieldPath = field.field.split('.');
        }
        let value = field.fieldPath.reduce((current, key) => {
                        return current?.[key];
                    }, obj);
        if (value && field.type === 'date') { 
            value = AppUtils.DateTime.GetDateTimeString(
                        new DateForPmis(value),
                        'YYYY-MM-DD HH:mm'
                    );
        }
        return value;
    }

    public static setValueByField(obj: any, field: IField, value: any): void {
        if (obj === null || obj === undefined || !field?.field) {
            return;
        }
        
        if (!field.fieldPath) {
            field.fieldPath = field.field.split('.');
        }
        
        let current = obj;
        const path = field.fieldPath;
        
        // Navigate to the parent of the target property
        for (let i = 0; i < path.length - 1; i++) {
            const key = path[i];
            
            // Create nested objects if they don't exist
            if (current[key] === null || current[key] === undefined || typeof current[key] !== 'object') {
                current[key] = {};
            }
            current = current[key];
        }
        
        // Set the final property
        const finalKey = path[path.length - 1];
        current[finalKey] = value;
    }

    /***
     * Simple deep copy, ignore datetime type parsing.
     */
    public static DeepCopySimple(obj: any): any {
        if (obj === undefined || obj === null || !(obj instanceof Object))
            return obj;
        return JSON.parse(JSON.stringify(obj));
    }

    public static JsonArrayToCsv(data: any[], keys: string[] = [], includeHeader = true): string {
        if (!data || data.length === 0) return '';

        if (keys.length === 0) {
            keys = Object.keys(data[0]);
        }

        const lines: string[] = [];
        if (includeHeader) {
            lines.push(keys.map(k => `"${k}"`).join(','));
        }
        data.forEach(d => {
            const line = keys.map(k => {
                let v = d[k];
                if (v === null || v === undefined) v = '';
                else if (v instanceof Date) v = AppUtils.DateTime.GetDateTimeString(v, 'YYYY-MM-DD HH:mm');
                else v = v.toString().replace(/"/g, '""').trim();
                return `"${v}"`;
            }).join(',');
            lines.push(line);
        });
        return lines.join('\n');
    }
}
